import React, { useEffect, useState } from 'react';
import { ArrowRight, Play, Zap, TrendingUp, Shield, Sparkles } from 'lucide-react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center pt-20 pb-16 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left Column - Content */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

            {/* Category Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium text-cyan-400">A New Category in HFT</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight mb-4">
              <span className="text-shimmer text-5xl sm:text-6xl lg:text-7xl">Smart + Fast</span>
              <br />
              <span className="text-white">Not Just Fast</span>
            </h1>

            {/* World's First Tagline */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="px-3 py-1 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 text-yellow-400 text-sm font-bold">
                  FIRST KNOWN
                </span>
                <span className="text-lg text-muted-foreground">
                  <span className="text-cyan-400 font-semibold">Codon-Native</span> HFT Decision Engine
                </span>
              </div>
            </div>

            {/* Subheadline */}
            <p className="text-lg text-muted-foreground mb-8 max-w-lg">
              The <span className="text-white font-semibold">first known pure-software decision engine processing 950M+ decisions/sec</span>.
              Achieved on commodity hardware — designed for <span className="text-cyan-400 font-bold">continuous hot-path execution</span> on co-located production servers.
            </p>

            {/* Key Stats Row */}
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">950M/s</div>
                  <div className="text-xs text-muted-foreground">Decision Throughput</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">954M/s</div>
                  <div className="text-xs text-muted-foreground">Decisions/sec</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">~1ns*</div>
                  <div className="text-xs text-muted-foreground">Amortized per-decision</div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href="#contact"
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-lg font-semibold rounded-xl transition-all flex items-center justify-center gap-3 overflow-hidden shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative">Get In Touch</span>
                <ArrowRight className="relative w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#demo"
                className="group px-8 py-4 border border-white/20 text-white text-lg font-medium rounded-xl hover:border-cyan-500/50 hover:bg-white/5 transition-all flex items-center justify-center gap-3"
              >
                <Play className="w-5 h-5 text-cyan-400" />
                <span>See Benchmarks</span>
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                SEBI-Ready Architecture
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                NSE Co-location Target
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                60+ Global Exchanges
              </span>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className={`relative transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

            {/* The Problem/Solution Card */}
            <div className="relative p-8 rounded-3xl bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border border-white/10 shadow-2xl">

              {/* Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 rounded-3xl blur-xl opacity-50" />

              <div className="relative">
                {/* Header */}
                <div className="text-center mb-6">
                  <h3 className="text-lg font-bold text-white mb-2">The Market Gap</h3>
                  <p className="text-sm text-muted-foreground">Why ASTRA is a new category</p>
                </div>

                {/* Comparison Grid */}
                <div className="space-y-4 mb-6">
                  {/* FPGA Row */}
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-red-400">FPGA Solutions</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400">Fast but Blind</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      100-500ns tick-to-trade, but limited to simple hardcoded logic. Cannot run real quant strategies.
                    </p>
                  </div>

                  {/* Software Row */}
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-yellow-400">Software Solutions</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">Smart but Slow</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Flexible and programmable, but microsecond latencies can't compete with FPGA speeds.
                    </p>
                  </div>

                  {/* ASTRA Row */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-green-500/10 border border-cyan-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-cyan-400">ASTRA</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-400">Smart + Fast</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <span className="text-white font-medium">950M decisions/sec</span> with pluggable quant models. Target architecture: 150-250ns tick-to-trade with FPGA offload.
                    </p>
                  </div>
                </div>

                {/* Bottom Stats */}
                <div className="pt-6 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-cyan-400">6</div>
                    <div className="text-xs text-muted-foreground">Protocols</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-cyan-400">11</div>
                    <div className="text-xs text-muted-foreground">Months R&D</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-cyan-400">15K+</div>
                    <div className="text-xs text-muted-foreground">Lines of Code</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-4 left-0 sm:-left-4 px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium shadow-lg">
              Verified on Apple M1 Pro & 5-year-old Windows laptop
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-8 text-center max-w-3xl mx-auto">
          <p className="text-xs text-muted-foreground px-4 py-2 rounded-lg bg-white/5 border border-white/10 inline-block">
            <span className="text-yellow-400 font-semibold">Benchmark:</span> 954M decisions/sec on ARM64, 450M on x86. *Amortized per-decision time from pipelined throughput. First known pure-software engine achieving near-billion decisions/sec.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
